import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { SidebarModal } from '../../components/common/Sidebar/Sidebar';
import {
  formatUSD,
  formatDecimalNumber,
  getWalletBalance,
  getAkashaWalletBalanceFromUltron,
  getULXWalletBalance,
  getAkashaWalletBalanceFromBase
} from '../../helpers/helperFunctions';
import { CaptionText, H3, P1, P3 } from '../../styles/FontStyles';
import { Button, Flexed } from '../../styles/GlobalComponents';
import { color } from '../../styles/GlobalStyles';
import {
  Icon,
  IconPortfolioDistribution,
  IconPortfolioOrders,
  IconPortfolioWallet
} from '../../styles/Icons';
import { settingsInit } from '../Settings/Settings.actions';

import { BoxPortfolio, SummaryGrid } from './Portfolio.styled';
import { Wallet } from './Wallet';
import { Withdraw } from './Withdraw/Withdraw';
import { WithdrawSteps, WithdrawAvailability } from './Portfolio.types';
import { Contract, ethers } from 'ethers';

import erc20Abi from '../../assets/abi/erc20.json';
import { ErrorToast } from '../../components/common/Toasts/Toasts';
import { getAkashaWalletSummary } from '../Marketplace/Marketplace.actions';

export const WalletSummary = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('my_hubs');

  const [step, setStep] = useState<WithdrawSteps>(WithdrawSteps.INITIAL);
  const [withdraw, setWithdraw] = useState(false);
  const [withdrawAvailabilitySteps, setWithdrawAvailabilitySteps] = useState(
    WithdrawAvailability.ULX
  );
  const [ulxBalance, setULXBalance] = useState<any>(0);
  const [wlxBalance, setWlxBalance] = useState<any>(0);
  const [usdtBalance, setUsdtBalance] = useState<any>(0);

  const { walletSummary, withdrawAvailability } = useAppSelector(
    (state) => state.portfolio
  );

  const getBalance = async () => {
    if (!walletSummary || !walletSummary.userAddress) return;

    const wULXAddress = process.env.REACT_APP_WULX_ADDRESS!;
    const wULXDecimals = process.env.REACT_APP_WULX_DECIMALS!;

    const wulxBalance = await getWalletBalance(
      walletSummary.userAddress,
      wULXAddress,
      wULXDecimals
    );
    const usdtBalance = await getWalletBalance(
      walletSummary.userAddress,
      process.env.REACT_APP_USDT_ADDRESS!,
      process.env.REACT_APP_USDT_DECIMALS!
    );

    const akashaBalance = await getAkashaWalletBalanceFromUltron(
      walletSummary.userAddress,
      process.env.REACT_APP_AKASHA_ADDRESS!,
      process.env.REACT_APP_WULX_DECIMALS!
    );

    const akashaBalanceBase = await getAkashaWalletBalanceFromBase(
      walletSummary.userAddress,
      process.env.REACT_APP_AKASHA_ADDRESS_FROM_BASE!,
      process.env.REACT_APP_WULX_DECIMALS!
    );

    const ulxBalance = await getULXWalletBalance(walletSummary.userAddress);

    setWlxBalance(wulxBalance);
    setUsdtBalance(usdtBalance);

    setULXBalance(ulxBalance);
  };

  useEffect(() => {
    getBalance();
    dispatch(getAkashaWalletSummary());
  }, [dispatch]);

  return (
    <>
      <Flexed mb='20' between alignEnd rowSM alignStartSM gap='16px'>
        <H3>{t('my_wallet_summary')}</H3>
        <Wallet />
      </Flexed>
      <SummaryGrid>
        <BoxPortfolio>
          <Icon mr='16' mrSM='8' color={color.colors.secondary100}>
            <IconPortfolioOrders />
          </Icon>
          <div>
            <CaptionText caps bold color={color.colors.neutral500} mb='10'>
              {t('total_personal_orders')}
            </CaptionText>

            <P1 bold flex alignEnd gap='5px'>
              {formatUSD(walletSummary?.totalOrder)} <P3 mb='1'>USDT</P3>
            </P1>
          </div>
        </BoxPortfolio>

        <BoxPortfolio hasButton>
          <Flexed center>
            <Icon mr='16' mrSM='8' color={color.colors.secondary100}>
              <IconPortfolioWallet />
            </Icon>
            <div>
              <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                {t('available_in_wallet')}
              </CaptionText>
              <P1 bold flex alignEnd gap='5px'>
                {formatUSD(Number(usdtBalance) ? Number(usdtBalance) : 0)}
                <P3 mb='1'>uUSDT</P3>
              </P1>
            </div>
          </Flexed>

          <Button
            w='100px'
            h='34px'
            disabled={walletSummary && Number(usdtBalance) < 1}
            onClick={() => {
              setWithdrawAvailabilitySteps(WithdrawAvailability.USDT);
              if (withdrawAvailability?.isAvailable) {
                setWithdraw(true);
                setStep(1);
                dispatch(settingsInit());
              } else {
                setWithdraw(true);
                setStep(9);
              }
            }}>
            {t('withdraw')}
          </Button>
        </BoxPortfolio>

        <BoxPortfolio hasButton>
          <Flexed center>
            <Icon mr='16' mrSM='8' color={color.colors.secondary100}>
              <IconPortfolioWallet />
            </Icon>
            <div>
              <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                {t('available_in_wallet')}
              </CaptionText>
              <P1 bold flex alignEnd gap='5px'>
                {formatUSD(Number(ulxBalance))}
                <P3 mb='1'>ULX</P3>
              </P1>
            </div>
          </Flexed>
        </BoxPortfolio>

        <BoxPortfolio hasButton>
          <Flexed center>
            <Icon mr='16' mrSM='8' color={color.colors.secondary100}>
              <IconPortfolioWallet />
            </Icon>
            <div>
              <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                {t('available_in_wallet')}
              </CaptionText>
              <P1 bold flex alignEnd gap='5px'>
                {formatUSD(Number(wlxBalance))}
                <P3 mb='1'>wULX</P3>
              </P1>
            </div>
          </Flexed>
          <Button
            w='100px'
            h='34px'
            disabled={walletSummary && Number(wlxBalance) < 1}
            onClick={() => {
              setWithdrawAvailabilitySteps(WithdrawAvailability.ULX);
              if (withdrawAvailability?.isAvailable) {
                setWithdraw(true);
                setStep(1);
                dispatch(settingsInit());
              } else {
                setWithdraw(true);
                setStep(9);
              }
            }}>
            {t('withdraw')}
          </Button>
        </BoxPortfolio>
      </SummaryGrid>

      <SidebarModal
        isOpen={withdraw}
        setOpen={setWithdraw}
        title={`${
          withdrawAvailabilitySteps === WithdrawAvailability.ULX
            ? 'wULX'
            : 'uUSDT'
        } ${t('withdrawal')}`}
        sidebarWidth={'508px'}>
        <Withdraw
          step={step}
          setStep={setStep}
          setWithdraw={setWithdraw}
          withdrawAvailabilitySteps={withdrawAvailabilitySteps}
        />
      </SidebarModal>
    </>
  );
};
